// Lightweight guided tour engine for the main host app.
// No persistence — guides always restart from step 1.
// Mount <MiniTourProvider> in the app shell; descendants call useMiniTour().
// Guide configs live in ./app-guide-registry.ts.
// Rendering is delegated to the shared SpotlightOverlay in ./tour-engine.tsx.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { SpotlightOverlay } from "./tour-engine";
import { APP_GUIDE_REGISTRY, type TourConfig, type TourStep } from "./app-guide-registry";

// Minimum gap (ms) between two step transitions. Guards against duplicate
// nextStep/prevStep calls within a single tick — e.g. the SpotlightOverlay's
// missing-target auto-advance racing a manual Next click, which previously
// made one click look like two step advances.
const ADVANCE_DEBOUNCE_MS = 300;

// Steps appended to a guide only when their `target` is actually present in the
// DOM at guide-start. Used for audience-specific panels on a shared page — the
// Sell listing detail page shows the owner a "Buyer Inquiries" inbox but shows
// buyers a "Contact Seller" CTA, so each step is injected only for the matching
// audience and never points at nothing for the other.
function domConditionalSteps(cfg: TourConfig): TourStep[] {
  return [cfg.ownerScopedStep, cfg.buyerScopedStep].filter(Boolean) as TourStep[];
}

// ─── Context ────────────────────────────────────────────────────────────────

interface MiniTourContextValue {
  activeTour: TourConfig | null;
  stepIndex: number;
  hasGuide: (pageKey: string | null | undefined) => boolean;
  startGuide: (pageKey: string) => void;
  stopGuide: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipGuide: () => void;
}

const MiniTourContext = createContext<MiniTourContextValue | null>(null);

const NOOP_TOUR_CONTEXT: MiniTourContextValue = {
  activeTour: null,
  stepIndex: -1,
  hasGuide: () => false,
  startGuide: () => undefined,
  stopGuide: () => undefined,
  nextStep: () => undefined,
  prevStep: () => undefined,
  skipGuide: () => undefined,
};

export function useMiniTour(): MiniTourContextValue {
  const ctx = useContext(MiniTourContext);
  // Outside the provider (e.g. in unit tests that render admin components directly),
  // return a no-op context so GuideButton renders silently as hidden rather than throwing.
  return ctx ?? NOOP_TOUR_CONTEXT;
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function MiniTourProvider({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const isAppRoute = location.startsWith("/app/") || location === "/app";
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(-1);
  // Whether the active guide was opened on a page reached from a specific
  // property (the URL carried `?propertyId=`). Captured once at startGuide time
  // so the injected property-context step stays stable for the whole walkthrough
  // even if the host later clears the property filter mid-guide.
  const [propertyScoped, setPropertyScoped] = useState(false);
  // DOM-presence-conditional steps to append to the active guide. Captured once
  // at startGuide time by checking which optional anchors are currently in the
  // DOM (e.g. the Sell listing page shows the owner an inbox but buyers a
  // "Contact Seller" CTA — each gets its own step for the matching audience).
  const [domScopedSteps, setDomScopedSteps] = useState<TourStep[]>([]);
  const lastAdvanceRef = useRef(0);

  const baseTour = activeTourId ? APP_GUIDE_REGISTRY[activeTourId] ?? null : null;

  // When opened from a property, splice the guide's property-context step in
  // right after the heading step so the host sees the page is scoped to one
  // property. When the page is showing an owner-only panel, append the guide's
  // owner-scoped step at the end. Otherwise the guide runs exactly as authored.
  const activeTour = useMemo<TourConfig | null>(() => {
    if (!baseTour) return null;
    const injectProperty = propertyScoped && Boolean(baseTour.propertyScopedStep);
    if (!injectProperty && domScopedSteps.length === 0) return baseTour;
    const steps = [...baseTour.steps];
    if (injectProperty) {
      steps.splice(Math.min(1, steps.length), 0, baseTour.propertyScopedStep!);
    }
    steps.push(...domScopedSteps);
    return { ...baseTour, steps };
  }, [baseTour, propertyScoped, domScopedSteps]);

  const stopGuide = useCallback(() => {
    setActiveTourId(null);
    setStepIndex(-1);
    setPropertyScoped(false);
    setDomScopedSteps([]);
    lastAdvanceRef.current = 0;
  }, []);

  const startGuide = useCallback((pageKey: string) => {
    const cfg = APP_GUIDE_REGISTRY[pageKey];
    if (!cfg) {
      console.warn(`[guide] no registered guide with pageKey "${pageKey}"`);
      return;
    }
    let scoped = false;
    if (cfg.propertyScopedStep) {
      try {
        scoped = Boolean(new URLSearchParams(window.location.search).get("propertyId"));
      } catch {
        scoped = false;
      }
    }
    setPropertyScoped(scoped);
    const present = domConditionalSteps(cfg).filter((step) => {
      try {
        return Boolean(document.querySelector(step.target));
      } catch {
        return false;
      }
    });
    setDomScopedSteps(present);
    setActiveTourId(pageKey);
    setStepIndex(0);
    // Reset (not stamp) so the first advance — including a missing-target
    // auto-advance from the SpotlightOverlay — is never blocked.
    lastAdvanceRef.current = 0;
  }, []);

  const skipGuide = stopGuide;

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    const now = Date.now();
    if (now - lastAdvanceRef.current < ADVANCE_DEBOUNCE_MS) return;
    lastAdvanceRef.current = now;
    if (stepIndex >= activeTour.steps.length - 1) {
      stopGuide();
      return;
    }
    setStepIndex((i) => i + 1);
  }, [activeTour, stepIndex, stopGuide]);

  const prevStep = useCallback(() => {
    if (!activeTour || stepIndex <= 0) return;
    const now = Date.now();
    if (now - lastAdvanceRef.current < ADVANCE_DEBOUNCE_MS) return;
    lastAdvanceRef.current = now;
    setStepIndex((i) => i - 1);
  }, [activeTour, stepIndex]);

  const hasGuide = useCallback((pageKey: string | null | undefined) => {
    if (!pageKey) return false;
    return Boolean(APP_GUIDE_REGISTRY[pageKey]);
  }, []);

  // Stop any active guide when navigating away from /app/* routes.
  useEffect(() => {
    if (!isAppRoute && activeTourId !== null) {
      setActiveTourId(null);
      setStepIndex(-1);
      setPropertyScoped(false);
    }
  }, [isAppRoute, activeTourId]);

  // Esc = close, ←/→ = back/next (only active on /app/* routes).
  useEffect(() => {
    if (!activeTour || !isAppRoute) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); skipGuide(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); nextStep(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prevStep(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeTour, isAppRoute, nextStep, prevStep, skipGuide]);

  const value = useMemo<MiniTourContextValue>(() => ({
    activeTour,
    stepIndex,
    hasGuide,
    startGuide,
    stopGuide,
    nextStep,
    prevStep,
    skipGuide,
  }), [activeTour, stepIndex, hasGuide, startGuide, stopGuide, nextStep, prevStep, skipGuide]);

  return (
    <MiniTourContext.Provider value={value}>
      {children}
      {isAppRoute && activeTour && stepIndex >= 0 && (
        <SpotlightOverlay
          tour={activeTour}
          stepIndex={stepIndex}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipGuide}
          onClose={skipGuide}
          testIdPrefix="guide"
          actionButtonClass="bg-emerald-600 text-white hover:bg-emerald-700"
          skipLabel="Skip guide"
        />
      )}
    </MiniTourContext.Provider>
  );
}
